import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logoIcon from '@/assets/logo-orange.svg';
import { useAuthAction } from '@/hooks/useAuthAction';
import LoginModal from '@/components/LoginModal/LoginModal';

const linkClass = 'font-manrope text-[15px] font-semibold text-gray-900 transition-opacity hover:opacity-70';

/**
 * Top marketing nav shown to signed-out visitors at md+ (desktop) in place of
 * the persistent left nav rail. Renders the logomark plus the three primary
 * marketing sections and the Log In / Sign Up CTAs. Explore is the in-app
 * destinations page (/destinations); Creators and About are same-origin
 * paths served by Squarespace via Vercel path rewrites (see vercel.json).
 * Both auth CTAs open the shared LoginModal via the standard useAuthAction
 * flow so redirect + analytics stay consistent.
 */
const LoggedOutTopNav: React.FC = () => {
  const { pathname } = useLocation();
  const { handleSignUpClick, showLoginModal, setShowLoginModal, loginModalRedirectPath } = useAuthAction();
  const [initialMode, setInitialMode] = useState<'login' | 'signup'>('signup');

  const openAuth = (mode: 'login' | 'signup') => {
    setInitialMode(mode);
    handleSignUpClick(pathname || '/');
  };

  return (
    <>
      <nav
        aria-label="Primary"
        className="hidden md:flex sticky top-0 z-header w-full items-center justify-between bg-white/95 px-8 py-4 backdrop-blur-sm shadow-search-bar"
      >
        <Link to="/" className="flex items-center" aria-label="Globe Thrivers home">
          <img src={logoIcon} alt="Globe Thrivers" className="h-10 w-auto" />
        </Link>
        <div className="flex items-center gap-8">
          <ul className="flex items-center gap-8">
            <li>
              <Link to="/destinations" className={linkClass}>
                Explore
              </Link>
            </li>
            <li>
              <a href="/for-creators" className={linkClass}>
                Creators
              </a>
            </li>
            <li>
              <a href="/about" className={linkClass}>
                About
              </a>
            </li>
          </ul>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => openAuth('login')}
              className="rounded-full px-5 py-2 font-manrope text-[14px] font-semibold text-gray-900 transition-colors hover:bg-gray-100"
            >
              Log In
            </button>
            <button
              type="button"
              onClick={() => openAuth('signup')}
              className="rounded-full bg-orange-500 px-5 py-2 font-manrope text-[14px] font-semibold text-white transition-colors hover:bg-orange-500-hover"
            >
              Sign Up
            </button>
          </div>
        </div>
      </nav>
      {showLoginModal && (
        <LoginModal
          onClose={() => {
            setShowLoginModal(false);
            setInitialMode('signup');
          }}
          initialMode={initialMode}
          redirectPath={loginModalRedirectPath}
        />
      )}
    </>
  );
};

export default LoggedOutTopNav;
